import React, { useRef, useLayoutEffect, useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { sanitizeUserName } from '../utils/security';
import { normalizeForSearch, searchTokens } from '../utils/searchHelpers';
import { fetchRandevuBedeliOnayBekleyenSayisi, RANDEVU_BEDELI_SAYAC_YENILE_EVENT } from '../services/randevuBedeliService';
import { fetchBekleyenIslemOzet, BEKLEYEN_ISLEM_YENILE_EVENT } from '../services/adminBekleyenIslemService';
import { fetchBayilikBasvuruSekmeSayilari, BAYILIK_BASVURU_SAYAC_YENILE_EVENT } from '../services/bayilikBasvuruService';
import { fetchTsKayitBasvuruSekmeSayilari, TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT } from '../services/tsKayitBasvuruService';
import { FieldLabel } from './FieldLabel';
import SidebarSearch from './SidebarSearch';

const NAV_SEARCH_DEBOUNCE_MS = 200;

/** Menü link etiketinde token bazlı TR duyarsız alt dize eşleşmesi */
const matchesNavItemLabel = (label, queryRaw) => {
  const tokens = searchTokens(queryRaw);
  if (!tokens.length) return true;
  const hay = normalizeForSearch(label);
  if (!hay) return false;
  return tokens.every((token) => hay.includes(token));
};

const applySidebarNavFilter = (navEl, queryRaw) => {
  if (!navEl) return { empty: false };

  const tokens = searchTokens(queryRaw);
  if (!tokens.length) {
    navEl.querySelectorAll('.nav-section, .nav-item').forEach((el) => {
      el.hidden = false;
    });
    return { empty: false };
  }

  let totalVisible = 0;
  navEl.querySelectorAll('.nav-section').forEach((section) => {
    let sectionHasVisible = false;

    section.querySelectorAll('.nav-item').forEach((item) => {
      const label = item.querySelector('span')?.textContent?.trim() || '';
      const match = matchesNavItemLabel(label, queryRaw);
      item.hidden = !match;
      if (match) {
        sectionHasVisible = true;
        totalVisible += 1;
      }
    });

    section.hidden = !sectionHasVisible;
  });

  return { empty: totalVisible === 0 };
};

/** AP kenar çubuğu liste kaydırması — rota değişince layout yeniden mount olduğu için kalıcı tutulur */
const SIDEBAR_NAV_SCROLL_KEY = 'hlm-ap-sidebar-nav-scroll';

const Sidebar = ({ isOpen, currentUser, onLogout, activePage, onNavigate, user }) => {
  const location = useLocation();
  const sidebarNavRef = useRef(null);
  const prevDebouncedNavSearchRef = useRef('');
  const [randevuBedeliOnayBekleyenSayisi, setRandevuBedeliOnayBekleyenSayisi] = useState(0);
  const [bekleyenIslemSayisi, setBekleyenIslemSayisi] = useState(0);
  const [bayilikOnayBekleyenSayisi, setBayilikOnayBekleyenSayisi] = useState(0);
  const [servisOnayBekleyenSayisi, setServisOnayBekleyenSayisi] = useState(0);
  const [navSearchInput, setNavSearchInput] = useState('');
  const [debouncedNavSearch, setDebouncedNavSearch] = useState('');
  const [navSearchEmpty, setNavSearchEmpty] = useState(false);

  const loadRandevuBedeliSayilari = useCallback(async () => {
    if (user?.rol !== 'admin') {
      setRandevuBedeliOnayBekleyenSayisi(0);
      return;
    }
    try {
      const onayBekleyen = await fetchRandevuBedeliOnayBekleyenSayisi();
      setRandevuBedeliOnayBekleyenSayisi(onayBekleyen);
    } catch {
      setRandevuBedeliOnayBekleyenSayisi(0);
    }
  }, [user?.rol]);

  const loadBayilikOnaySayisi = useCallback(async () => {
    if (!['admin', 'mod'].includes(user?.rol)) {
      setBayilikOnayBekleyenSayisi(0);
      return;
    }
    try {
      const res = await fetchBayilikBasvuruSekmeSayilari();
      setBayilikOnayBekleyenSayisi(res?.veri?.onay_bekliyor ?? 0);
    } catch {
      setBayilikOnayBekleyenSayisi(0);
    }
  }, [user?.rol]);

  const loadServisOnaySayisi = useCallback(async () => {
    if (!['admin', 'mod'].includes(user?.rol)) {
      setServisOnayBekleyenSayisi(0);
      return;
    }
    try {
      const res = await fetchTsKayitBasvuruSekmeSayilari();
      setServisOnayBekleyenSayisi((res?.veri?.onay_bekliyor ?? 0) + (res?.veri?.notlar ?? 0));
    } catch {
      setServisOnayBekleyenSayisi(0);
    }
  }, [user?.rol]);

  useEffect(() => {
    loadRandevuBedeliSayilari();
    const onRefresh = () => loadRandevuBedeliSayilari();
    window.addEventListener(RANDEVU_BEDELI_SAYAC_YENILE_EVENT, onRefresh);
    return () => window.removeEventListener(RANDEVU_BEDELI_SAYAC_YENILE_EVENT, onRefresh);
  }, [loadRandevuBedeliSayilari, location.pathname]);

  useEffect(() => {
    loadBayilikOnaySayisi();
    const onRefresh = () => loadBayilikOnaySayisi();
    window.addEventListener(BAYILIK_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
    return () => window.removeEventListener(BAYILIK_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
  }, [loadBayilikOnaySayisi, location.pathname]);

  useEffect(() => {
    loadServisOnaySayisi();
    const onRefresh = () => loadServisOnaySayisi();
    window.addEventListener(TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
    return () => window.removeEventListener(TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
  }, [loadServisOnaySayisi, location.pathname]);

  useLayoutEffect(() => {
    const el = sidebarNavRef.current;
    if (!el) return;
    try {
      const raw = sessionStorage.getItem(SIDEBAR_NAV_SCROLL_KEY);
      if (raw != null) {
        const y = Number(raw);
        if (!Number.isNaN(y)) el.scrollTop = y;
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const el = sidebarNavRef.current;
    if (!el) return;
    const save = () => {
      try {
        sessionStorage.setItem(SIDEBAR_NAV_SCROLL_KEY, String(el.scrollTop));
      } catch {
        /* ignore */
      }
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      el.removeEventListener('scroll', save);
    };
  }, []);

  /** iPad Safari: sayfa scroll sonrası sidebar kaydırması için WebKit scroll view'i yeniden etkinleştir */
  useEffect(() => {
    const nav = sidebarNavRef.current;
    const sidebar = document.getElementById('sidebar');
    if (!nav || !sidebar || !('ontouchstart' in window)) return undefined;

    const activateIosScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = nav;
      if (scrollHeight <= clientHeight) return;
      if (scrollTop <= 0) nav.scrollTop = 1;
      else if (scrollTop + clientHeight >= scrollHeight) nav.scrollTop = scrollTop - 1;
    };

    sidebar.addEventListener('touchstart', activateIosScroll, { passive: true });
    return () => sidebar.removeEventListener('touchstart', activateIosScroll);
  }, []);
  // Kullanıcı adını sanitize et (XSS koruması)
  const safeCurrentUser = sanitizeUserName(currentUser);
  
  // Admin kontrolü
  const isAdmin = user?.rol === 'admin';
  // Admin veya moderator kontrolü
  const isAdminOrModerator = user?.rol === 'admin' || user?.rol === 'mod' || user?.rol === 'moderator';
  const isSales = user?.rol === 'sales';

  const loadBekleyenIslemSayisi = useCallback(async () => {
    if (!isAdminOrModerator) {
      setBekleyenIslemSayisi(0);
      return;
    }
    try {
      const res = await fetchBekleyenIslemOzet();
      setBekleyenIslemSayisi(res?.veri?.toplam ?? 0);
    } catch {
      setBekleyenIslemSayisi(0);
    }
  }, [isAdminOrModerator]);

  useEffect(() => {
    loadBekleyenIslemSayisi();
    const onRefresh = () => loadBekleyenIslemSayisi();
    window.addEventListener(BEKLEYEN_ISLEM_YENILE_EVENT, onRefresh);
    return () => window.removeEventListener(BEKLEYEN_ISLEM_YENILE_EVENT, onRefresh);
  }, [loadBekleyenIslemSayisi, location.pathname]);

  // Teknik servis veya teknisyen (tech-person) kullanıcısı kontrolü
  const userRole = user?.rol?.toLowerCase?.() || user?.rol || '';
  const isTechService = userRole === 'tech-service' ||
                        userRole === 'teknikservis' || 
                        userRole === 'techservice' || 
                        userRole === 'teknik-servis' ||
                        user?.rol === 'tech-service' ||
                        user?.rol === 'teknikServis' || 
                        user?.rol === 'techService' ||
                        user?.rol === 'TeknikServis' ||
                        user?.rol === 'TechService';
  const isTechPerson = userRole === 'tech-person' || user?.rol === 'tech-person';
  const isSevkiyat = userRole === 'sevkiyat' || user?.rol === 'sevkiyat';
  const isTechServiceOrPerson = isTechService || isTechPerson;
  const showApNavSearch = !isTechServiceOrPerson && !isSevkiyat;
  const navSearchPending = showApNavSearch
    && navSearchInput.trim().length > 0
    && navSearchInput !== debouncedNavSearch;

  useEffect(() => {
    if (!showApNavSearch) {
      setDebouncedNavSearch('');
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setDebouncedNavSearch(navSearchInput);
    }, NAV_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [navSearchInput, showApNavSearch]);

  useLayoutEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;
    if (!showApNavSearch) {
      nav.querySelectorAll('.nav-section, .nav-item').forEach((el) => {
        el.hidden = false;
      });
      setNavSearchEmpty((prev) => (prev ? false : prev));
      return;
    }
    const { empty } = applySidebarNavFilter(nav, debouncedNavSearch);
    setNavSearchEmpty((prev) => (prev === empty ? prev : empty));
    if (debouncedNavSearch.trim() && debouncedNavSearch !== prevDebouncedNavSearchRef.current) {
      nav.scrollTop = 0;
    }
    prevDebouncedNavSearchRef.current = debouncedNavSearch;
  });

  const handleNavSearchClear = useCallback(() => {
    setNavSearchInput('');
    setDebouncedNavSearch('');
    setNavSearchEmpty(false);
  }, []);

  const handleNavigation = (pageId) => {
    if (onNavigate && typeof onNavigate === 'function') {
      onNavigate(pageId);
    }
  };

  // URL'den active page'i belirle
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'active' : ''}`} id="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/assets/img/logo.png" alt="Firma Logo" />
          </div>
          <h2 className="sidebar-title">SM Yönetim Sistemi</h2>
        </div>
        <div className="sidebar-divider" aria-hidden="true"></div>

        {showApNavSearch && (
          <SidebarSearch
            value={navSearchInput}
            onChange={setNavSearchInput}
            onClear={handleNavSearchClear}
            isPending={navSearchPending}
          />
        )}

        <nav className="sidebar-nav" ref={sidebarNavRef}>
          {showApNavSearch && navSearchEmpty && !navSearchPending && (
            <div className="sidebar-nav-empty">Sonuç yok</div>
          )}
          {/* Anasayfa: teknisyen / sevkiyat kendi paneline, diğerleri / */}
          {!isSevkiyat && (
          <div className="nav-section">
            <Link
              to={isTechPerson ? '/teknisyen' : '/'}
              className={`nav-item ${isTechPerson ? (location.pathname === '/teknisyen' ? 'active' : '') : (isActive('/') ? 'active' : '')}`}
              onClick={() => handleNavigation(isTechPerson ? 'teknisyen' : 'home')}
              aria-current={isTechPerson ? (location.pathname === '/teknisyen' ? 'page' : undefined) : (isActive('/') ? 'page' : undefined)}
            >
              <i className="fas fa-home"></i>
              <span>Anasayfa</span>
            </Link>
          </div>
          )}

          {/* Sevkiyat Paneli */}
          {isSevkiyat && (
            <div className="nav-section">
              <FieldLabel className="nav-section-title" as="div">Sevkiyat Paneli</FieldLabel>
              <Link
                to="/sevkiyat/siparisler"
                className={`nav-item ${isActive('/sevkiyat/siparisler') ? 'active' : ''}`}
                onClick={() => handleNavigation('sevkiyat-siparisler')}
                aria-current={isActive('/sevkiyat/siparisler') ? 'page' : undefined}
              >
                <i className="fas fa-truck"></i>
                <span>Sipariş Kargoları</span>
              </Link>
              <Link
                to="/sevkiyat/iade-kargolari"
                className={`nav-item ${isActive('/sevkiyat/iade-kargolari') ? 'active' : ''}`}
                onClick={() => handleNavigation('sevkiyat-iade-kargolari')}
                aria-current={isActive('/sevkiyat/iade-kargolari') ? 'page' : undefined}
              >
                <i className="fas fa-box-open"></i>
                <span>İade Kargoları</span>
              </Link>
              <Link
                to="/sevkiyat/gonderimler"
                className={`nav-item ${isActive('/sevkiyat/gonderimler') ? 'active' : ''}`}
                onClick={() => handleNavigation('sevkiyat-gonderimler')}
                aria-current={isActive('/sevkiyat/gonderimler') ? 'page' : undefined}
              >
                <i className="fas fa-exchange-alt"></i>
                <span>Gönderimler</span>
              </Link>
            </div>
          )}

          {/* Teknisyen Paneli - sadece tech-person */}
          {isTechPerson && (
            <div className="nav-section">
              <FieldLabel className="nav-section-title" as="div">Teknisyen Paneli</FieldLabel>
              <Link
                to="/teknisyen/randevular"
                className={`nav-item ${isActive('/teknisyen/randevular') ? 'active' : ''}`}
                onClick={() => handleNavigation('teknisyen-randevular')}
                aria-current={isActive('/teknisyen/randevular') ? 'page' : undefined}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>Randevularım</span>
              </Link>
              <Link
                to="/teknisyen/randevu-test"
                className={`nav-item ${isActive('/teknisyen/randevu-test') ? 'active' : ''}`}
                onClick={() => handleNavigation('teknisyen-randevu-test')}
                aria-current={isActive('/teknisyen/randevu-test') ? 'page' : undefined}
              >
                <i className="fas fa-vial"></i>
                <span>Randevu Test</span>
              </Link>
            </div>
          )}

          {/* Teknik servis paneli - sadece tech-service (teknisyen değil) */}
          {isTechService && !isTechPerson && (
            <>
              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">Servis</FieldLabel>
                <Link
                  to="/tech-service/service-records"
                  className={`nav-item ${isActive('/tech-service/service-records') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-service-records')}
                  aria-current={isActive('/tech-service/service-records') ? 'page' : undefined}
                >
                  <i className="fas fa-clipboard-list"></i>
                  <span>Servis Kayıtları</span>
                </Link>
                <Link
                  to="/appointments"
                  className={`nav-item ${isActive('/appointments') ? 'active' : ''}`}
                  onClick={() => handleNavigation('appointments')}
                  aria-current={isActive('/appointments') ? 'page' : undefined}
                >
                  <i className="fas fa-calendar-alt"></i>
                  <span>Randevular</span>
                </Link>
                <Link
                  to="/tech-service/teknisyenler"
                  className={`nav-item ${isActive('/tech-service/teknisyenler') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-teknisyenler')}
                  aria-current={isActive('/tech-service/teknisyenler') ? 'page' : undefined}
                >
                  <i className="fas fa-user-cog"></i>
                  <span>Teknisyenler</span>
                </Link>
                <Link
                  to="/tech-service/iade-kargolari"
                  className={`nav-item ${isActive('/tech-service/iade-kargolari') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-iade-kargolari')}
                  aria-current={isActive('/tech-service/iade-kargolari') ? 'page' : undefined}
                >
                  <i className="fas fa-undo"></i>
                  <span>İade Kargoları</span>
                </Link>
                <Link
                  to="/tech-service/cihaz-sorgulama"
                  className={`nav-item ${isActive('/tech-service/cihaz-sorgulama') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-cihaz-sorgulama')}
                  aria-current={isActive('/tech-service/cihaz-sorgulama') ? 'page' : undefined}
                >
                  <i className="fas fa-barcode"></i>
                  <span>Cihaz Sorgulama</span>
                </Link>
                <Link
                  to="/tech-service/cihaz-degisimi"
                  className={`nav-item ${isActive('/tech-service/cihaz-degisimi') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-cihaz-degisimi')}
                  aria-current={isActive('/tech-service/cihaz-degisimi') ? 'page' : undefined}
                >
                  <i className="fas fa-exchange-alt"></i>
                  <span>Cihaz Değişimi</span>
                </Link>
              </div>

              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">Cari ve Stok</FieldLabel>
                <Link
                  to="/tech-service/hakedisler"
                  className={`nav-item ${isActive('/tech-service/hakedisler') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-hakedisler')}
                  aria-current={isActive('/tech-service/hakedisler') ? 'page' : undefined}
                >
                  <i className="fas fa-file-invoice-dollar"></i>
                  <span>Hakedişler</span>
                </Link>
                <Link
                  to="/tech-service/cari-hesaplar"
                  className={`nav-item ${isActive('/tech-service/cari-hesaplar') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-cari-hesaplar')}
                  aria-current={isActive('/tech-service/cari-hesaplar') ? 'page' : undefined}
                >
                  <i className="fas fa-calculator"></i>
                  <span>Cari Hareketler</span>
                </Link>
                <Link
                  to="/tech-service/gonderimler"
                  className={`nav-item ${isActive('/tech-service/gonderimler') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-gonderimler')}
                  aria-current={isActive('/tech-service/gonderimler') ? 'page' : undefined}
                >
                  <i className="fas fa-truck-loading"></i>
                  <span>Gönderimler</span>
                </Link>
                <Link
                  to="/tech-service/siparisler"
                  className={`nav-item ${isActive('/tech-service/siparisler') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-siparisler')}
                  aria-current={isActive('/tech-service/siparisler') ? 'page' : undefined}
                >
                  <i className="fas fa-shopping-cart"></i>
                  <span>Siparişler</span>
                </Link>
                <Link
                  to="/tech-service/stok-kullanim"
                  className={`nav-item ${isActive('/tech-service/stok-kullanim') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-stok-kullanim')}
                  aria-current={isActive('/tech-service/stok-kullanim') ? 'page' : undefined}
                >
                  <i className="fas fa-hand-holding"></i>
                  <span>Stok Yönetimi</span>
                </Link>
                <Link
                  to="/tech-service/iade-faturalari"
                  className={`nav-item ${isActive('/tech-service/iade-faturalari') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-iade-faturalari')}
                  aria-current={isActive('/tech-service/iade-faturalari') ? 'page' : undefined}
                >
                  <i className="fas fa-file-invoice"></i>
                  <span>İade Faturaları</span>
                </Link>
              </div>

              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">Yönetim</FieldLabel>
                <Link
                  to="/tech-service/siparis-onaylari"
                  className={`nav-item ${isActive('/tech-service/siparis-onaylari') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-siparis-onaylari')}
                  aria-current={isActive('/tech-service/siparis-onaylari') ? 'page' : undefined}
                >
                  <i className="fas fa-clipboard-check"></i>
                  <span>Sipariş Onayları</span>
                </Link>
                <Link
                  to="/tech-service/kullanicilar"
                  className={`nav-item ${isActive('/tech-service/kullanicilar') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-kullanicilar')}
                  aria-current={isActive('/tech-service/kullanicilar') ? 'page' : undefined}
                >
                  <i className="fas fa-users"></i>
                  <span>Kullanıcı Yönetimi</span>
                </Link>
              </div>

              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">Bildirimler</FieldLabel>
                <Link
                  to="/tech-service/duyurular"
                  className={`nav-item ${isActive('/tech-service/duyurular') ? 'active' : ''}`}
                  onClick={() => handleNavigation('tech-service-duyurular')}
                  aria-current={isActive('/tech-service/duyurular') ? 'page' : undefined}
                >
                  <i className="fas fa-envelope-open-text"></i>
                  <span>Mesajlar</span>
                </Link>
              </div>
            </>
          )}

          {/* Admin/Mod/Sales vb. — kenar çubuğu grupları */}
          {!isTechServiceOrPerson && !isSevkiyat && (
            <>
              {!isSales && (
              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">Yönetim</FieldLabel>
                <Link
                  to="/users"
                  className={`nav-item ${isActive('/users') ? 'active' : ''}`}
                  onClick={() => handleNavigation('users')}
                  aria-current={isActive('/users') ? 'page' : undefined}
                >
                  <i className="fas fa-users"></i>
                  <span>Kullanıcı Yönetimi</span>
                </Link>
                {isAdminOrModerator && (
                  <Link
                    to="/bayilik-onaylari"
                    className={`nav-item ${isActive('/bayilik-onaylari') ? 'active' : ''}`}
                    onClick={() => handleNavigation('bayilik-onaylari')}
                    aria-current={isActive('/bayilik-onaylari') ? 'page' : undefined}
                  >
                    <i className="fas fa-store"></i>
                    <span>Bayilik Onayları</span>
                    {bayilikOnayBekleyenSayisi > 0 && (
                      <span
                        className="nav-item-count-badge"
                        aria-label={`${bayilikOnayBekleyenSayisi} bayilik onayı`}
                      >
                        {bayilikOnayBekleyenSayisi > 99 ? '99+' : bayilikOnayBekleyenSayisi}
                      </span>
                    )}
                  </Link>
                )}
                {isAdminOrModerator && (
                  <Link
                    to="/servis-onaylari"
                    className={`nav-item ${isActive('/servis-onaylari') ? 'active' : ''}`}
                    onClick={() => handleNavigation('servis-onaylari')}
                    aria-current={isActive('/servis-onaylari') ? 'page' : undefined}
                  >
                    <i className="fas fa-user-cog"></i>
                    <span>Servis Onayları</span>
                    {servisOnayBekleyenSayisi > 0 && (
                      <span
                        className="nav-item-count-badge"
                        aria-label={`${servisOnayBekleyenSayisi} servis onayı`}
                      >
                        {servisOnayBekleyenSayisi > 99 ? '99+' : servisOnayBekleyenSayisi}
                      </span>
                    )}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/tech-services"
                    className={`nav-item ${isActive('/tech-services') ? 'active' : ''}`}
                    onClick={() => handleNavigation('tech-services')}
                    aria-current={isActive('/tech-services') ? 'page' : undefined}
                  >
                    <i className="fas fa-tools"></i>
                    <span>Teknik Servis Yönetimi</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/bayiler"
                    className={`nav-item ${isActive('/bayiler') ? 'active' : ''}`}
                    onClick={() => handleNavigation('bayiler')}
                    aria-current={isActive('/bayiler') ? 'page' : undefined}
                  >
                    <i className="fas fa-store"></i>
                    <span>Bayi Yönetimi</span>
                  </Link>
                )}
                {isAdminOrModerator && (
                  <Link
                    to="/customers"
                    className={`nav-item ${isActive('/customers') ? 'active' : ''}`}
                    onClick={() => handleNavigation('customers')}
                    aria-current={isActive('/customers') ? 'page' : undefined}
                  >
                    <i className="fas fa-user-friends"></i>
                    <span>Müşteri Yönetimi</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/sms"
                    className={`nav-item ${isActive('/sms') ? 'active' : ''}`}
                    onClick={() => handleNavigation('sms')}
                    aria-current={isActive('/sms') ? 'page' : undefined}
                  >
                    <i className="fas fa-sms"></i>
                    <span>SMS Yönetimi</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/ayarlar"
                    className={`nav-item ${isActive('/ayarlar') ? 'active' : ''}`}
                    onClick={() => handleNavigation('ayarlar')}
                    aria-current={isActive('/ayarlar') ? 'page' : undefined}
                  >
                    <i className="fas fa-sliders-h"></i>
                    <span>Ayarlar</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/duyurular"
                    className={`nav-item ${isActive('/duyurular') ? 'active' : ''}`}
                    onClick={() => handleNavigation('duyurular')}
                    aria-current={isActive('/duyurular') ? 'page' : undefined}
                  >
                    <i className="fas fa-bullhorn"></i>
                    <span>Duyurular</span>
                  </Link>
                )}
              </div>
              )}

              {isAdmin && (
                <div className="nav-section">
                  <FieldLabel className="nav-section-title" as="div">Cari</FieldLabel>
                  <Link
                    to="/hakedisler"
                    className={`nav-item ${isActive('/hakedisler') ? 'active' : ''}`}
                    onClick={() => handleNavigation('hakedisler')}
                    aria-current={isActive('/hakedisler') ? 'page' : undefined}
                  >
                    <i className="fas fa-file-invoice-dollar"></i>
                    <span>Hakedişler</span>
                  </Link>
                  <Link
                    to="/cari-hesaplar"
                    className={`nav-item ${isActive('/cari-hesaplar') ? 'active' : ''}`}
                    onClick={() => handleNavigation('cari-hesaplar')}
                    aria-current={isActive('/cari-hesaplar') ? 'page' : undefined}
                  >
                    <i className="fas fa-calculator"></i>
                    <span>Cari Hareketler</span>
                  </Link>
                  <Link
                    to="/iade-faturalari"
                    className={`nav-item ${isActive('/iade-faturalari') ? 'active' : ''}`}
                    onClick={() => handleNavigation('iade-faturalari')}
                    aria-current={isActive('/iade-faturalari') ? 'page' : undefined}
                  >
                    <i className="fas fa-file-invoice"></i>
                    <span>İade Faturaları</span>
                  </Link>
                </div>
              )}

              {isAdminOrModerator && (
                <div className="nav-section">
                  <FieldLabel className="nav-section-title" as="div">Sipariş ve Randevu</FieldLabel>
                  <Link
                    to="/bekleyen-islemler"
                    className={`nav-item ${isActive('/bekleyen-islemler') ? 'active' : ''}`}
                    onClick={() => handleNavigation('bekleyen-islemler')}
                    aria-current={isActive('/bekleyen-islemler') ? 'page' : undefined}
                  >
                    <i className="fas fa-tasks"></i>
                    <span>Bekleyen İşlemler</span>
                    {bekleyenIslemSayisi > 0 && (
                      <span
                        className="nav-item-count-badge"
                        aria-label={`${bekleyenIslemSayisi} bekleyen işlem`}
                      >
                        {bekleyenIslemSayisi > 99 ? '99+' : bekleyenIslemSayisi}
                      </span>
                    )}
                  </Link>
                  {isAdminOrModerator && (
                    <Link
                      to="/completed-appointments"
                      className={`nav-item ${isActive('/completed-appointments') ? 'active' : ''}`}
                      onClick={() => handleNavigation('completed-appointments')}
                      aria-current={isActive('/completed-appointments') ? 'page' : undefined}
                    >
                      <i className="fas fa-calendar-check"></i>
                      <span>Randevular</span>
                    </Link>
                  )}
                  {isAdminOrModerator && (
                    <Link
                      to="/anketler"
                      className={`nav-item ${isActive('/anketler') ? 'active' : ''}`}
                      onClick={() => handleNavigation('anketler')}
                      aria-current={isActive('/anketler') ? 'page' : undefined}
                    >
                      <i className="fas fa-poll"></i>
                      <span>Anketler</span>
                    </Link>
                  )}
                  {isAdminOrModerator && (
                    <Link
                      to="/odeme-problemli-randevular"
                      className={`nav-item ${isActive('/odeme-problemli-randevular') ? 'active' : ''}`}
                      onClick={() => handleNavigation('odeme-problemli-randevular')}
                      aria-current={isActive('/odeme-problemli-randevular') ? 'page' : undefined}
                    >
                      <i className="fas fa-exclamation-circle"></i>
                      <span>Ödeme Problemli</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/randevu-bedelleri"
                      className={`nav-item ${isActive('/randevu-bedelleri') ? 'active' : ''}`}
                      onClick={() => handleNavigation('randevu-bedelleri')}
                      aria-current={isActive('/randevu-bedelleri') ? 'page' : undefined}
                    >
                      <i className="fas fa-money-bill-wave"></i>
                      <span>Randevu Bedelleri</span>
                      {(randevuBedeliOnayBekleyenSayisi > 0) && (
                        <span
                          className="nav-item-count-badge"
                          aria-label={`${randevuBedeliOnayBekleyenSayisi} onay bekleyen randevu bedeli`}
                        >
                          {randevuBedeliOnayBekleyenSayisi > 99 ? '99+' : randevuBedeliOnayBekleyenSayisi}
                        </span>
                      )}
                    </Link>
                  )}
                  <Link
                    to="/siparis-takip"
                    className={`nav-item ${isActive('/siparis-takip') ? 'active' : ''}`}
                    onClick={() => handleNavigation('siparis-takip')}
                    aria-current={isActive('/siparis-takip') ? 'page' : undefined}
                  >
                    <i className="fas fa-shopping-cart"></i>
                    <span>Sipariş Takip Sistemi</span>
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/stok-yonetimi"
                      className={`nav-item ${isActive('/stok-yonetimi') ? 'active' : ''}`}
                      onClick={() => handleNavigation('stok-yonetimi')}
                      aria-current={isActive('/stok-yonetimi') ? 'page' : undefined}
                    >
                      <i className="fas fa-boxes"></i>
                      <span>Stok Yönetimi</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/gonderimler"
                      className={`nav-item ${isActive('/gonderimler') ? 'active' : ''}`}
                      onClick={() => handleNavigation('gonderimler')}
                      aria-current={isActive('/gonderimler') ? 'page' : undefined}
                    >
                      <i className="fas fa-truck-loading"></i>
                      <span>Gönderimler</span>
                    </Link>
                  )}
                  <Link
                    to="/iade-kargolari"
                    className={`nav-item ${isActive('/iade-kargolari') ? 'active' : ''}`}
                    onClick={() => handleNavigation('iade-kargolari')}
                    aria-current={isActive('/iade-kargolari') ? 'page' : undefined}
                  >
                    <i className="fas fa-undo"></i>
                    <span>İade Kargoları</span>
                  </Link>
                  <Link
                    to="/cihaz-degisimi"
                    className={`nav-item ${isActive('/cihaz-degisimi') ? 'active' : ''}`}
                    onClick={() => handleNavigation('cihaz-degisimi')}
                    aria-current={isActive('/cihaz-degisimi') ? 'page' : undefined}
                  >
                    <i className="fas fa-exchange-alt"></i>
                    <span>Cihaz Değişimi</span>
                  </Link>
                </div>
              )}

              {(isAdminOrModerator || isSales) && (
                <div className="nav-section">
                  <FieldLabel className="nav-section-title" as="div">Servis</FieldLabel>
                    <Link
                      to="/service-records/create"
                      className={`nav-item ${isActive('/service-records/create') ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation('service-records-create');
                      }}
                      aria-current={isActive('/service-records/create') ? 'page' : undefined}
                    >
                      <i className="fas fa-clipboard-list"></i>
                      <span>Servis Kaydı Oluştur</span>
                    </Link>
                    <Link
                      to="/service-records"
                      className={`nav-item ${isActive('/service-records') && !isActive('/service-records/create') && !isActive('/completed-appointments') && !isActive('/gorusme-kayitlari') ? 'active' : ''}`}
                      onClick={() => handleNavigation('service-records')}
                      aria-current={isActive('/service-records') && !isActive('/service-records/create') && !isActive('/completed-appointments') && !isActive('/gorusme-kayitlari') ? 'page' : undefined}
                    >
                      <i className="fas fa-tasks"></i>
                      <span>Servis Kaydı Takip</span>
                    </Link>
                    <Link
                      to="/complaint-records"
                      className={`nav-item ${isActive('/complaint-records') ? 'active' : ''}`}
                      onClick={() => handleNavigation('complaint-records')}
                      aria-current={isActive('/complaint-records') ? 'page' : undefined}
                    >
                      <i className="fas fa-comment-dots"></i>
                      <span>İstek Şikayet Takibi</span>
                    </Link>
                    <Link
                      to="/on-servis-kayitlari"
                      className={`nav-item ${isActive('/on-servis-kayitlari') ? 'active' : ''}`}
                      onClick={() => handleNavigation('on-servis-kayitlari')}
                      aria-current={isActive('/on-servis-kayitlari') ? 'page' : undefined}
                    >
                      <i className="fas fa-file-medical"></i>
                      <span>Ön Servis Kayıtları</span>
                    </Link>
                    <Link
                      to="/gorusme-kayitlari"
                      className={`nav-item ${isActive('/gorusme-kayitlari') ? 'active' : ''}`}
                      onClick={() => handleNavigation('gorusme-kayitlari')}
                      aria-current={isActive('/gorusme-kayitlari') ? 'page' : undefined}
                    >
                      <i className="fas fa-phone-alt"></i>
                      <span>Görüşme Kayıtları</span>
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/musteri-hizmetleri"
                        className={`nav-item ${isActive('/musteri-hizmetleri') ? 'active' : ''}`}
                        onClick={() => handleNavigation('musteri-hizmetleri')}
                        aria-current={isActive('/musteri-hizmetleri') ? 'page' : undefined}
                      >
                        <i className="fas fa-headset"></i>
                        <span>Müşteri Hizmetleri</span>
                      </Link>
                    )}
                    <Link
                      to="/cihaz-sorgulama"
                      className={`nav-item ${isActive('/cihaz-sorgulama') ? 'active' : ''}`}
                      onClick={() => handleNavigation('cihaz-sorgulama')}
                      aria-current={isActive('/cihaz-sorgulama') ? 'page' : undefined}
                    >
                      <i className="fas fa-barcode"></i>
                      <span>Cihaz Sorgulama</span>
                    </Link>
                    <Link
                      to="/teknik-servisler"
                      className={`nav-item ${location.pathname === '/teknik-servisler' ? 'active' : ''}`}
                      onClick={() => handleNavigation('teknik-servisler')}
                      aria-current={location.pathname === '/teknik-servisler' ? 'page' : undefined}
                    >
                      <i className="fas fa-map-marked-alt"></i>
                      <span>Teknik Servisler</span>
                    </Link>
                </div>
              )}

              {!isSales && (
              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">İstatistik</FieldLabel>
                {isAdmin && (
                  <>
                    <Link
                      to="/istatistik/cihazlar"
                      className={`nav-item ${isActive('/istatistik/cihazlar') ? 'active' : ''}`}
                      onClick={() => handleNavigation('istatistik-cihazlar')}
                      aria-current={isActive('/istatistik/cihazlar') ? 'page' : undefined}
                    >
                      <i className="fas fa-box"></i>
                      <span>Cihaz</span>
                    </Link>
                    <Link
                      to="/istatistik/ariza"
                      className={`nav-item ${isActive('/istatistik/ariza') ? 'active' : ''}`}
                      onClick={() => handleNavigation('istatistik-ariza')}
                      aria-current={isActive('/istatistik/ariza') ? 'page' : undefined}
                    >
                      <i className="fas fa-chart-bar"></i>
                      <span>Arıza İstatistiği</span>
                    </Link>
                  </>
                )}
              </div>
              )}

              {isAdmin && (
                <div className="nav-section">
                  <FieldLabel className="nav-section-title" as="div">Sistem</FieldLabel>
                  <Link
                    to="/monitor"
                    className={`nav-item ${isActive('/monitor') ? 'active' : ''}`}
                    onClick={() => handleNavigation('monitor')}
                    aria-current={isActive('/monitor') ? 'page' : undefined}
                  >
                    <i className="fas fa-heartbeat"></i>
                    <span>Monitör</span>
                  </Link>
                  <Link
                    to="/dokumantasyon"
                    className={`nav-item ${isActive('/dokumantasyon') ? 'active' : ''}`}
                    onClick={() => handleNavigation('dokumantasyon')}
                    aria-current={isActive('/dokumantasyon') ? 'page' : undefined}
                  >
                    <i className="fas fa-book"></i>
                    <span>Dökümantasyon</span>
                  </Link>
                  <Link
                    to="/dosyalar"
                    className={`nav-item ${isActive('/dosyalar') ? 'active' : ''}`}
                    onClick={() => handleNavigation('dosyalar')}
                    aria-current={isActive('/dosyalar') ? 'page' : undefined}
                  >
                    <i className="fas fa-folder-open"></i>
                    <span>Dosyalar</span>
                  </Link>
                  <Link
                    to="/db-yedekleme"
                    className={`nav-item ${isActive('/db-yedekleme') ? 'active' : ''}`}
                    onClick={() => handleNavigation('db-yedekleme')}
                    aria-current={isActive('/db-yedekleme') ? 'page' : undefined}
                  >
                    <i className="fas fa-database"></i>
                    <span>DB Yedekleme</span>
                  </Link>
                  <Link
                    to="/test-veri-temizle"
                    className={`nav-item ${isActive('/test-veri-temizle') ? 'active' : ''}`}
                    onClick={() => handleNavigation('test-veri-temizle')}
                    aria-current={isActive('/test-veri-temizle') ? 'page' : undefined}
                  >
                    <i className="fas fa-eraser"></i>
                    <span>Test verilerini sil</span>
                  </Link>
                  <Link
                    to="/egitim-ts-kayitlari"
                    className={`nav-item ${isActive('/egitim-ts-kayitlari') ? 'active' : ''}`}
                    onClick={() => handleNavigation('egitim-ts-kayitlari')}
                    aria-current={isActive('/egitim-ts-kayitlari') ? 'page' : undefined}
                  >
                    <i className="fas fa-chalkboard-teacher"></i>
                    <span>Eğitim TS kayıtları</span>
                  </Link>
                  <Link
                    to="/sisteme-randevu-test"
                    className={`nav-item ${isActive('/sisteme-randevu-test') ? 'active' : ''}`}
                    onClick={() => handleNavigation('sisteme-randevu-test')}
                    aria-current={isActive('/sisteme-randevu-test') ? 'page' : undefined}
                  >
                    <i className="fas fa-vial"></i>
                    <span>Sisteme Randevu Test</span>
                  </Link>
                  <Link
                    to="/apk-surumleri"
                    className={`nav-item ${isActive('/apk-surumleri') ? 'active' : ''}`}
                    onClick={() => handleNavigation('apk-surumleri')}
                    aria-current={isActive('/apk-surumleri') ? 'page' : undefined}
                  >
                    <i className="fas fa-mobile-alt"></i>
                    <span>APK Sürümleri</span>
                  </Link>
                  <Link
                    to="/yapilacaklar"
                    className={`nav-item ${isActive('/yapilacaklar') ? 'active' : ''}`}
                    onClick={() => handleNavigation('yapilacaklar')}
                    aria-current={isActive('/yapilacaklar') ? 'page' : undefined}
                  >
                    <i className="fas fa-list-check"></i>
                    <span>Yapılacaklar</span>
                  </Link>
                  <Link
                    to="/teknik-servisler/il-etiketleri"
                    className={`nav-item ${location.pathname.startsWith('/teknik-servisler/il-etiketleri') ? 'active' : ''}`}
                    onClick={() => handleNavigation('teknik-servisler-etiket')}
                    aria-current={
                      location.pathname.startsWith('/teknik-servisler/il-etiketleri') ? 'page' : undefined
                    }
                  >
                    <i className="fas fa-font"></i>
                    <span>Harita Etiketleri</span>
                  </Link>
                </div>
              )}

              {!isSales && (
              <div className="nav-section">
                <FieldLabel className="nav-section-title" as="div">İçerik Yönetimi</FieldLabel>
                {isAdminOrModerator && (
                  <>
                    <Link
                      to="/contents/urun"
                      className={`nav-item ${location.pathname === '/contents/urun' ? 'active' : ''}`}
                      onClick={() => handleNavigation('contents-urun')}
                      aria-current={location.pathname === '/contents/urun' ? 'page' : undefined}
                    >
                      <i className="fas fa-box"></i>
                      <span>Ürün Yönetimi</span>
                    </Link>
                    <Link
                      to="/contents/parca"
                      className={`nav-item ${location.pathname === '/contents/parca' ? 'active' : ''}`}
                      onClick={() => handleNavigation('contents-parca')}
                      aria-current={location.pathname === '/contents/parca' ? 'page' : undefined}
                    >
                      <i className="fas fa-cogs"></i>
                      <span>Parça Yönetimi</span>
                    </Link>
                    <Link
                      to="/contents/islem"
                      className={`nav-item ${location.pathname === '/contents/islem' ? 'active' : ''}`}
                      onClick={() => handleNavigation('contents-islem')}
                      aria-current={location.pathname === '/contents/islem' ? 'page' : undefined}
                    >
                      <i className="fas fa-wrench"></i>
                      <span>İşlem Yönetimi</span>
                    </Link>
                    <Link
                      to="/contents/ariza"
                      className={`nav-item ${location.pathname === '/contents/ariza' ? 'active' : ''}`}
                      onClick={() => handleNavigation('contents-ariza')}
                      aria-current={location.pathname === '/contents/ariza' ? 'page' : undefined}
                    >
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Arıza Yönetimi</span>
                    </Link>
                    <Link
                      to="/contents/bakim-yonetimi"
                      className={`nav-item ${location.pathname === '/contents/bakim-yonetimi' ? 'active' : ''}`}
                      onClick={() => handleNavigation('contents-bakim-yonetimi')}
                      aria-current={location.pathname === '/contents/bakim-yonetimi' ? 'page' : undefined}
                    >
                      <i className="fas fa-wrench"></i>
                      <span>Bakım Yönetimi</span>
                    </Link>
                    {isAdmin && (
                    <Link
                      to="/checklists"
                      className={`nav-item ${isActive('/checklists') ? 'active' : ''}`}
                      onClick={() => handleNavigation('checklists')}
                      aria-current={isActive('/checklists') ? 'page' : undefined}
                    >
                      <i className="fas fa-list-check"></i>
                      <span>Checklistler</span>
                    </Link>
                    )}
                    <Link
                      to="/training-videos"
                      className={`nav-item ${isActive('/training-videos') ? 'active' : ''}`}
                      onClick={() => handleNavigation('training-videos')}
                      aria-current={isActive('/training-videos') ? 'page' : undefined}
                    >
                      <i className="fas fa-video"></i>
                      <span>Eğitimler</span>
                    </Link>
                  </>
                )}
              </div>
              )}
            </>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar"></div>
            <span className="user-name">{safeCurrentUser}</span>
          </div>
          <Link
            to="/hesap/oturumlar"
            className={`nav-item sidebar-sessions-link ${isActive('/hesap/oturumlar') ? 'active' : ''}`}
            title="Aktif oturumlar"
            aria-label="Aktif oturumlar"
            onClick={() => handleNavigation('hesap-oturumlar')}
          >
            <i className="fas fa-shield-alt"></i>
          </Link>
          <button
            type="button"
            className="logout-btn"
            title="Çıkış"
            aria-label="Çıkış yap"
            onClick={(e) => {
              e.preventDefault();
              if (onLogout && typeof onLogout === 'function') {
                onLogout();
              }
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

